import React, { useEffect, useState } from 'react';

export interface PolicyDocument {
  title: string;
  /** Bundled asset URL of the Markdown document (assets/docs/pelora-*.md). */
  url: string;
}

interface PolicyDocumentDialogProps {
  policy: PolicyDocument | null;
  onClose: () => void;
}

/**
 * In-app viewer for the Terms of Service and Privacy Policy. Shared by the sign-in page
 * links and the ToS/Privacy acceptance dialog so both show the documents the same way.
 * z-[60] keeps it above other z-50 dialogs (the acceptance dialog opens it on top of itself).
 */
const PolicyDocumentDialog: React.FC<PolicyDocumentDialogProps> = ({ policy, onClose }) => {
  const [content, setContent] = useState('');

  useEffect(() => {
    if (!policy) {
      setContent('');
      return;
    }
    let active = true;
    setContent('Loading...');
    // Static bundled asset, not an API call
    fetch(policy.url)
      .then(response => {
        if (!response.ok) throw new Error('Unable to load policy');
        return response.text();
      })
      .then(text => { if (active) setContent(text); })
      .catch(() => { if (active) setContent('This document could not be loaded. Please try again.'); });
    return () => { active = false; };
  }, [policy]);

  if (!policy) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="policy-dialog-title"
      onClick={event => event.target === event.currentTarget && onClose()}
    >
      <div className="flex max-h-[90vh] w-full max-w-3xl flex-col rounded-lg bg-white p-6 shadow-xl">
        <h2 id="policy-dialog-title" className="mb-4 text-xl font-semibold text-slate-800">
          {policy.title}
        </h2>
        <pre className="min-h-0 flex-1 overflow-y-auto whitespace-pre-wrap rounded border border-slate-200 bg-slate-50 p-4 text-left text-sm leading-6 text-slate-700">
          {content}
        </pre>
        <button type="button" onClick={onClose} className="mt-4 self-end rounded-md bg-slate-800 px-6 py-2 text-sm font-medium text-white hover:bg-slate-700">
          OK
        </button>
      </div>
    </div>
  );
};

export default PolicyDocumentDialog;
